import { formatCreditos } from '../lib/creditos.js';
import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tag,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { useAuthStore } from '../stores/authStore.js';
import { getStaffTenantUsage, type TenantDetailResult } from '../api/staffUsage.js';
import { ApiClientError } from '../api/client.js';
import {
  formatUsd,
  fmtDate,
  fmtMonth,
  monthRangeFromInput,
  todayIsoEndOfDay,
  monthsAgoIso,
} from '../lib/staffUsageFormat.js';
import { MonthlyBarChart } from '../components/MonthlyBarChart.js';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  content: { flex: 1, padding: '24px 32px', maxWidth: '1280px', width: '100%', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' },
  back: { color: tokens.colorBrandForegroundLink, textDecoration: 'none', fontSize: tokens.fontSizeBase300 },
  title: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold },
  subtitle: { color: tokens.colorNeutralForeground3, marginBottom: '20px' },
  filters: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '20px' },
  filterField: { minWidth: '160px' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' },
  kpiCard: { padding: '14px 18px', background: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  kpiLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginBottom: '4px' },
  kpiValue: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold, fontVariantNumeric: 'tabular-nums' },

  twoCol: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '20px', marginBottom: '24px', '@media (max-width: 900px)': { gridTemplateColumns: '1fr' } },
  panel: { background: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: '16px 20px' },
  panelTitle: { fontWeight: tokens.fontWeightSemibold, marginBottom: '12px' },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  thRight: { textAlign: 'right' },
  td: { padding: '8px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: tokens.fontSizeBase300 },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  link: { color: tokens.colorBrandForegroundLink, textDecoration: 'none', ':hover': { textDecoration: 'underline' } },

  chartWrap: { background: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: '16px 20px', marginBottom: '24px' },
});

const PLAN_LABEL: Record<string, string> = {
  start: 'Start',
  pro: 'Pro',
  teams: 'Teams',
  vitruvio: 'Vitruvio',
};

const FEATURE_LABEL: Record<string, string> = {
  chat: 'Chat',
  summary: 'Resumen',
  quote: 'Cotización',
  apu_ia: 'APU-IA',
  embedding: 'Embeddings',
  web_search: 'Búsqueda web',
  vision: 'Lectura de imágenes',
  other: 'Otro',
};

export function StaffTenantUsagePage() {
  const styles = useStyles();
  const { tenantId } = useParams<{ tenantId: string }>();
  const tenantInAuth = useAuthStore((s) => s.tenant);
  const isStaff =
    !!tenantInAuth?.isEcosoftOwned &&
    (tenantInAuth.role === 'admin' || tenantInAuth.role === 'owner');

  const [from, setFrom] = useState<string>(() => monthsAgoIso(11));
  const [to, setTo] = useState<string>(() => todayIsoEndOfDay());
  const [data, setData] = useState<TenantDetailResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isStaff || !tenantId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStaffTenantUsage(tenantId, {
      from: monthRangeFromInput(from, 'start'),
      to: monthRangeFromInput(to, 'end'),
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiClientError ? e.message : 'Error al cargar tenant');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, tenantId, from, to]);

  if (!isStaff) return <Navigate to="/" replace />;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs
        items={[
          { label: 'Inicio', to: '/projects' },
          { label: 'Staff' },
          { label: 'Consumo por tenant', to: '/staff/usage' },
          { label: 'Tenant' },
        ]}
      />
      <main className={styles.content}>
        <div className={styles.header}>
          <Link to="/staff/usage" className={styles.back}>← Volver</Link>
          <Text as="h1" className={styles.title}>
            {data?.tenant.slug ?? '…'}
          </Text>
          {data && <Tag size="small">{data.tenant.plan ? PLAN_LABEL[data.tenant.plan] ?? data.tenant.plan : '—'}</Tag>}
        </div>
        {data && (
          <div className={styles.subtitle}>
            {data.tenant.name}
            {data.tenant.subscriptionExpiresAt && (
              <> · Suscripción: {fmtDate(data.tenant.subscriptionStartedAt)} → {fmtDate(data.tenant.subscriptionExpiresAt)}</>
            )}
          </div>
        )}

        <div className={styles.filters}>
          <Field label="Desde" className={styles.filterField}>
            <Input type="date" value={from.slice(0, 10)} onChange={(_e, d) => setFrom(d.value)} />
          </Field>
          <Field label="Hasta" className={styles.filterField}>
            <Input type="date" value={to.slice(0, 10)} onChange={(_e, d) => setTo(d.value)} />
          </Field>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: 16 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {loading && <Spinner size="small" label="Cargando…" />}

        {!loading && data && (
          <>
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>$ período</div>
                <div className={styles.kpiValue}>{formatUsd(data.tenant.periodCostUsd)}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>$ anual (ciclo suscripción)</div>
                <div className={styles.kpiValue}>{formatUsd(data.tenant.annualCostUsd)}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Turnos LLM</div>
                <div className={styles.kpiValue}>{data.tenant.periodTurns.toLocaleString('es-MX')}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Tokens (in/out)</div>
                <div className={styles.kpiValue} style={{ fontSize: tokens.fontSizeBase400 }}>
                  {(data.tenant.periodInputTokens / 1000).toFixed(1)}k / {(data.tenant.periodOutputTokens / 1000).toFixed(1)}k
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Créditos (consumo real)</div>
                <div className={styles.kpiValue} style={{ fontSize: tokens.fontSizeBase400 }}>
                  {formatCreditos(data.tenant.periodInputTokens + data.tenant.periodOutputTokens, 'desglose')}
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Usuarios activos</div>
                <div className={styles.kpiValue}>
                  {data.users.filter((u) => u.periodTurns > 0).length}
                </div>
              </div>
            </div>

            <div className={styles.chartWrap}>
              <div className={styles.panelTitle}>Costo mensual (USD)</div>
              <MonthlyBarChart data={data.monthly} />
            </div>

            <div className={styles.twoCol}>
              <div className={styles.panel}>
                <div className={styles.panelTitle}>Por modelo</div>
                <BreakdownTable rows={data.byModel} />
              </div>
              <div className={styles.panel}>
                <div className={styles.panelTitle}>Por feature</div>
                <BreakdownTable
                  rows={data.byFeature.map((r) => ({ ...r, key: FEATURE_LABEL[r.key] ?? r.key }))}
                />
              </div>
            </div>

            <div className={styles.panel}>
              <div className={styles.panelTitle}>Usuarios</div>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Usuario</th>
                    <th className={styles.th}>Rol</th>
                    <th className={styles.th}>Plan</th>
                    <th className={`${styles.th} ${styles.thRight}`}>$ período</th>
                    <th className={`${styles.th} ${styles.thRight}`}>$ anual</th>
                    <th className={`${styles.th} ${styles.thRight}`}>Turnos</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.userId}>
                      <td className={styles.td}>
                        <Link to={`/staff/usage/tenants/${tenantId}/users/${u.userId}`} className={styles.link}>
                          {u.email}
                        </Link>
                        {u.name && <span style={{ marginLeft: 8, color: tokens.colorNeutralForeground3 }}>{u.name}</span>}
                      </td>
                      <td className={styles.td}>{u.role ?? '—'}</td>
                      <td className={styles.td}>{u.plan ? PLAN_LABEL[u.plan] ?? u.plan : '—'}</td>
                      <td className={`${styles.td} ${styles.tdRight}`}>{formatUsd(u.periodCostUsd)}</td>
                      <td className={`${styles.td} ${styles.tdRight}`}>{formatUsd(u.annualCostUsd)}</td>
                      <td className={`${styles.td} ${styles.tdRight}`}>{u.periodTurns.toLocaleString('es-MX')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function BreakdownTable({ rows }: { rows: { key: string; costUsd: number; turns: number; inputTokens: number; outputTokens: number }[] }) {
  const styles = useStyles();
  if (rows.length === 0) {
    return <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Sin datos en este período.</Text>;
  }
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.th}></th>
          <th className={`${styles.th} ${styles.thRight}`}>$</th>
          <th className={`${styles.th} ${styles.thRight}`}>Turnos</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td className={styles.td}>{r.key}</td>
            <td className={`${styles.td} ${styles.tdRight}`}>{formatUsd(r.costUsd)}</td>
            <td className={`${styles.td} ${styles.tdRight}`}>{r.turns.toLocaleString('es-MX')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
